import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { UserPlus, UserCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";

const FollowProfileButton = ({
  targetUserId,
  onChange,
}: {
  targetUserId: string;
  onChange?: (following: boolean) => void;
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: isFollowing = false, isLoading } = useQuery({
    queryKey: ["profile-follow", user?.id, targetUserId],
    queryFn: async () => {
      if (!user) return false;
      const { data } = await supabase
        .from("profile_followers")
        .select("id")
        .eq("follower_user_id", user.id)
        .eq("following_user_id", targetUserId)
        .maybeSingle();
      return !!data;
    },
    enabled: !!user && !!targetUserId,
  });

  const toggle = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("auth");
      if (isFollowing) {
        const { error } = await supabase
          .from("profile_followers")
          .delete()
          .eq("follower_user_id", user.id)
          .eq("following_user_id", targetUserId);
        if (error) throw error;
        return false;
      } else {
        const { error } = await supabase
          .from("profile_followers")
          .insert({ follower_user_id: user.id, following_user_id: targetUserId });
        if (error) throw error;
        return true;
      }
    },
    onSuccess: (nowFollowing) => {
      queryClient.invalidateQueries({ queryKey: ["profile-follow", user?.id, targetUserId] });
      queryClient.invalidateQueries({ queryKey: ["profile-follower-count", targetUserId] });
      onChange?.(nowFollowing);
      toast({
        title: nowFollowing ? "Following" : "Unfollowed",
        description: nowFollowing
          ? "You'll get notified when they post a new idea."
          : "You won't get notified about their new ideas.",
      });
    },
    onError: (err: any) => {
      toast({ title: "Could not update", description: err.message, variant: "destructive" });
    },
  });

  if (!user) {
    return (
      <Button variant="outline" size="sm" onClick={() => navigate("/auth")} className="gap-2">
        <UserPlus className="h-4 w-4" /> Follow
      </Button>
    );
  }

  if (user.id === targetUserId) return null;

  return (
    <Button
      variant={isFollowing ? "secondary" : "default"}
      size="sm"
      disabled={isLoading || toggle.isPending}
      onClick={() => toggle.mutate()}
      className="gap-2"
    >
      {isFollowing ? <UserCheck className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />}
      {isFollowing ? "Following" : "Follow"}
    </Button>
  );
};

export default FollowProfileButton;